import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {offersReadAloud} from '#/lib/spokenKinds'
import {
  getFlashBookPlayPalette,
  useFlashBookPlayDesign,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {FlashBookReadAloud} from '#/components/flashbooks/FlashBookReadAloud'
import {FlashBookRichBlock} from '#/components/flashbooks/FlashBookRichBlock'
import {type FlashBookDesign} from '#/themes-flashbook/design'
import {type Slide} from '#/types/capsule'
import {type FlashBookThemeTokens} from '#/types/flashbook'

type Props = {
  block: Slide
  accent: string
  /** Book theme tokens: text colours and light/dark come from the book. */
  tokens?: FlashBookThemeTokens
  /** Book theme design: typography and the read-aloud treatment. */
  design?: FlashBookDesign
  /** Which translated label the read-aloud control uses. */
  audioLabel?: FlashBookDesign['audio']['label']
  /** Called when the learner finished reading (the clip ended / mark as read). */
  onRead?: () => void
}

/**
 * FlashBook content block — the reading side of a page.
 *
 * A plain `content` block is title + prose and sets no surface of its own: it
 * uses the theme's title/body type scale and presents the read-aloud action in
 * the theme's own voice — a quiet line under the prose in a storybook, a compact
 * transport in a study theme, a squared badge in an editorial one.
 *
 * A `content` block carrying a `contentKind` is one of the *rich reading blocks*
 * (quote, note, captioned figure, table, formula). Those are handed to
 * `FlashBookRichBlock`, which reads its treatment from the theme's design
 * language — so the same pull quote is a flourished aside in Storybook, a
 * margin note in Notebook and a bracketed log entry in Space Adventure.
 */
export function FlashBookContentBlock({
  block,
  accent,
  tokens,
  design,
  audioLabel,
  onRead,
}: Props) {
  const {t: l} = useLingui()
  // The design may also arrive through context (block previews); the prop wins.
  const ctxDesign = useFlashBookPlayDesign()
  const d = design ?? ctxDesign

  const kind = block.contentKind ?? 'text'
  const isRich = kind !== 'text'

  const body = block.contentText ?? ''
  const title = block.contentTitle ?? ''

  // Outside a book (the capsule studio's preview) there are no tokens; the play
  // palette resolves to its own defaults so nothing regresses there. Every ink
  // comes from that palette rather than a literal: these three used to spell two
  // of its values out by hand, which meant a book whose theme colours its own
  // text could still have its headings forced white.
  const pal = getFlashBookPlayPalette(tokens)
  const headingColor = pal.text
  const bodyColor = pal.body
  const mutedColor = pal.muted

  // Whether this block has a voice to offer. The whole question, asked of one
  // place (see `#/lib/spokenKinds`): the kind has to be writing rather than
  // notation, *and* the creator has to have recorded or uploaded the reading. A
  // passage nobody narrated prints no transport at all — there is no synthesised
  // voice underneath it — so the same rule answers for both studios, the book
  // reader and the capsule player.
  const speaks = offersReadAloud(block)

  // On ruled paper the controls are printed on the page too, so the space above
  // them is a whole line of it: 14px of app margin leaves the transport — and
  // the "mark as read" under it — floating between two lines.
  const rule =
    d.block.mode === 'ruled'
      ? (d.page.textureStep ?? d.type.body.lineHeight)
      : 0
  const controlsStyle = rule > 0 ? {gap: rule, marginTop: rule} : undefined
  // And the fallback action is one line of the page tall, so the transport
  // above it and the next block below it both land on a line.
  const readBtnStyle =
    rule > 0 ? {minHeight: rule, paddingVertical: 0} : undefined

  return (
    <View>
      {isRich ? (
        <FlashBookRichBlock
          block={block}
          tokens={tokens}
          design={d}
          accent={accent}
        />
      ) : (
        <>
          {!!title && (
            <Text style={[d.type.title, {color: headingColor}]}>{title}</Text>
          )}
          {!!body && (
            <Text style={[d.type.body, {color: bodyColor}]}>{body}</Text>
          )}
        </>
      )}

      {speaks || onRead ? (
        <View style={[styles.controls, controlsStyle]}>
          {speaks && (
            <FlashBookReadAloud
              source={{url: block.readingAudioUrl!}}
              accent={accent}
              mutedColor={mutedColor}
              design={d}
              label={
                audioLabel === 'listen'
                  ? l`Listen`
                  : audioLabel === 'play-reading'
                    ? l`Play reading`
                    : audioLabel
                      ? l`Read to me`
                      : undefined
              }
              onEnded={onRead}
              style={styles.readAloud}
            />
          )}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Mark as read`}
            accessibilityHint={l`Records this page as read without playing the audio`}
            onPress={() => onRead?.()}
            style={[styles.readBtn, readBtnStyle]}>
            <Feather name="check" size={12} color={mutedColor} />
            <Text style={[styles.readBtnTxt, {color: mutedColor}]}>
              <Trans>Mark as read</Trans>
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  )
}

const styles = StyleSheet.create({
  controls: {
    gap: 10,
    marginTop: 14,
    alignItems: 'flex-start',
  },
  readAloud: {alignSelf: 'stretch'},
  // Quiet by design: "mark as read" is a fallback, not the primary action, so
  // it never competes with the read-aloud player or the question itself.
  readBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 4,
  },
  readBtnTxt: {fontSize: 12, fontWeight: '500'},
})
